/**
 * Dictation grader — note-by-note diff between the user's submitted
 * sequence and the exercise's `expectedSequence`.
 *
 * Pure: same inputs → same output, no I/O. Lives next to the
 * recognition-exercise grader (`gradeAnswer` in `generate.ts`); Practice
 * dispatches to one or the other based on `exercise.exerciseType`.
 *
 * Per-note status taxonomy (spec §8.4):
 *   - "correct"      degree (and alteration) match
 *   - "wrong-pitch"  degree+alteration mismatch
 *   - "wrong-octave" degree+alteration match but `octaveOffset` differs
 *                    AND `allowOctaveDisplacement` is false
 *   - "missing"      expected has a note, user didn't supply one
 *   - "extra"        user supplied a note past the expected length
 *
 * Octave displacement defaults to `true` (spec §8.1: "octave displacement
 * allowed in the entry-level node"). Solfège-mode input never carries
 * octave info, so strict-octave grading is meaningful only in piano /
 * MIDI input modes; the default keeps grading honest across all modes.
 *
 * FSRS rating mapping (spec §8.4):
 *   100% → "good"
 *   ≥75% → "hard"
 *   <75% → "again"
 *
 * `dictationRatingFromAccuracy` is exported alongside so Practice can
 * map directly without reading the same boundaries inline.
 */

import type { Exercise } from "./exercise.js";
import type { ScaleDegree, ScaleDegreeSequence } from "@music-app/theory-engine";

export type DictationNoteStatus =
  | "correct"
  | "wrong-pitch"
  | "wrong-octave"
  | "missing"
  | "extra";

export interface DictationGrading {
  /** Whole-melody correctness — every expected note matched, no extras. */
  correct: boolean;
  /**
   * One status per note in the *longer* of the two sequences. Length =
   * `max(expected.length, user.length)`. The first `expected.length`
   * entries describe the user's response to each expected note; any
   * trailing entries (if the user submitted more than expected) are
   * `extra`.
   */
  perNote: DictationNoteStatus[];
  /**
   * Index of the first non-correct entry in `perNote`, or `null` if the
   * user matched every expected note.
   */
  firstWrongIndex: number | null;
  /**
   * 0..1. `correctNotes / max(expected.length, user.length)` — divides
   * by the longer of the two so a user who pads extras past the target
   * doesn't get gifted extra credit.
   */
  accuracy: number;
  /** One-sentence sentence shown after submit. */
  explanation: string;
}

export interface GradeDictationOptions {
  /**
   * When true (default), notes match if the degree+alteration match,
   * regardless of `octaveOffset`. When false, strict-octave grading —
   * mismatched octaves register as `wrong-octave` rather than correct.
   *
   * Solfège-mode input has no octave info; strict-octave grading is
   * meaningful only in piano or MIDI modes.
   */
  allowOctaveDisplacement?: boolean;
}

/**
 * Local copy of FSRS's `Rating` string union. Keeps exercise-generator
 * independent of `@music-app/learning-system` at the package-graph
 * level — both packages stay foundation-pure and the only shared
 * dependency is `theory-engine`. Same string union as learning-system's
 * `Rating`.
 */
type LocalRating = "again" | "hard" | "good" | "easy";

/**
 * Map a dictation accuracy (0..1) → an FSRS-style rating. Spec §8.4:
 *
 *   1.00              → "good"  (perfect)
 *   ≥ 0.75            → "hard"  (mostly there, schedule sooner than usual)
 *   <  0.75           → "again" (revisit soon)
 *
 * "easy" is intentionally not used — dictation is an inherently
 * deliberate task, not a snap recognition, so "easy" wouldn't carry
 * the same "fast and confident" signal it carries for the recognition
 * exercises.
 */
export function dictationRatingFromAccuracy(accuracy: number): LocalRating {
  if (accuracy >= 1) return "good";
  if (accuracy >= 0.75) return "hard";
  return "again";
}

export function gradeDictation(
  exercise: Exercise,
  userSequence: ScaleDegreeSequence,
  opts: GradeDictationOptions = {},
): DictationGrading {
  const expected = exercise.meta.expectedSequence;
  if (!expected || expected.length === 0) {
    // Defensive — a dictation exercise with no expected sequence is a
    // bug in the generator; treat any submission as correct rather
    // than gaslighting the user.
    return {
      correct: true,
      perNote: userSequence.map(() => "extra"),
      firstWrongIndex: null,
      accuracy: 1,
      explanation: "Nothing to grade.",
    };
  }
  const allowOctave = opts.allowOctaveDisplacement ?? true;
  const len = Math.max(expected.length, userSequence.length);
  const perNote: DictationNoteStatus[] = [];
  let firstWrongIndex: number | null = null;
  let correctCount = 0;

  for (let i = 0; i < len; i++) {
    const exp = expected[i];
    const got = userSequence[i];
    let status: DictationNoteStatus;
    if (exp === undefined) {
      status = "extra";
    } else if (got === undefined) {
      status = "missing";
    } else if (sameDegreeAndAlteration(exp, got)) {
      if (allowOctave || sameOctaveOffset(exp, got)) {
        status = "correct";
      } else {
        status = "wrong-octave";
      }
    } else {
      status = "wrong-pitch";
    }
    perNote.push(status);
    if (status === "correct") correctCount += 1;
    else if (firstWrongIndex === null) firstWrongIndex = i;
  }

  const accuracy = len === 0 ? 1 : correctCount / len;
  const correct = correctCount === len && correctCount === expected.length;

  return {
    correct,
    perNote,
    firstWrongIndex,
    accuracy,
    explanation: buildDictationExplanation(perNote, accuracy, expected, userSequence),
  };
}

function sameDegreeAndAlteration(a: ScaleDegree, b: ScaleDegree): boolean {
  if (a.degree !== b.degree) return false;
  // `alteration: undefined` and absent both mean "natural" — normalize
  // before comparing.
  return (a.alteration ?? null) === (b.alteration ?? null);
}

function sameOctaveOffset(a: ScaleDegree, b: ScaleDegree): boolean {
  // Missing octaveOffset means "octave-agnostic" — treat as matching
  // any octave the other side specifies. Strict-octave callers in the
  // future can enforce both-sides-required; for now this is the
  // friendlier default.
  if (a.octaveOffset === undefined || b.octaveOffset === undefined) return true;
  return a.octaveOffset === b.octaveOffset;
}

function buildDictationExplanation(
  perNote: DictationNoteStatus[],
  accuracy: number,
  expected: ScaleDegreeSequence,
  user: ScaleDegreeSequence,
): string {
  if (accuracy >= 1) return "Nailed every note.";
  const wrongCount = perNote.filter((s) => s !== "correct").length;
  if (user.length === 0) return "No notes submitted.";
  if (user.length < expected.length) {
    return `${wrongCount} of ${expected.length} off — replay the slow target and try again.`;
  }
  if (user.length > expected.length) {
    return `${user.length - expected.length} extra note${
      user.length - expected.length === 1 ? "" : "s"
    } — listen for the melody's length, not just the contour.`;
  }
  return `${wrongCount} of ${expected.length} off — replay the slow target and try again.`;
}

/* ─── Re-export shape for the learning-system's `Rating` ─────────────── */

/**
 * Re-export so callers that already import the rating type from
 * `@music-app/exercise-generator` don't need a second import. Same
 * string union as learning-system's `Rating`.
 */
export type DictationRating = LocalRating;
